/**
 * The dev control strip — a fixed row of localhost-only chips over the header.
 *
 * Shared by `devViewport.js` and `devFixtures.js`, which is the whole reason it
 * exists: two floating controls each picking their own `top`/`left` is a magic
 * number waiting to overlap, and two copies of the same chip CSS is two places
 * to change it.
 *
 * NOT inside `#game-header`. Tempting, since that is where these visually sit,
 * but the header is `overflow: hidden` (so an extra control is silently clipped
 * rather than visibly wrong) and it is one of the surfaces the tablet pass is
 * most sensitive about. A fixed overlay owes the layout nothing and cannot
 * change how the real header behaves at any viewport.
 */

export function isLocal() {
    return typeof location !== 'undefined'
        && (location.hostname === 'localhost' || location.hostname === '127.0.0.1');
}

let bar = null;

/** The strip, created on first use. Null off localhost or without a DOM. */
export function devBar() {
    if (!isLocal() || typeof document === 'undefined') return null;
    if (bar) return bar;
    bar = document.createElement('div');
    bar.id = 'dev-bar';
    bar.style.cssText = [
        'position:fixed', 'top:2px', 'left:2px', 'z-index:2147483647',
        'display:flex', 'flex-wrap:wrap', 'gap:2px', 'align-items:center',
        // Dim at rest, awake on approach. These sit on top of the game at all
        // times during development, so they have to be findable without being
        // something the eye keeps catching while looking at the layout.
        'opacity:0.55', 'transition:opacity 0.12s',
    ].join(';');
    bar.addEventListener('mouseenter', () => { bar.style.opacity = '1'; });
    bar.addEventListener('mouseleave', () => { bar.style.opacity = '0.55'; });
    document.body.appendChild(bar);
    return bar;
}

/** A chip in the strip's house style. Returns the button so callers can restyle. */
export function devButton({ label, title, onClick }) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = label;
    btn.title = title;
    btn.style.cssText = [
        'font:700 10px/1 ui-monospace,monospace', 'padding:3px 5px',
        'color:#9ca3af', 'background:rgba(0,0,0,0.72)',
        'border:1px solid #374151', 'border-radius:4px', 'cursor:pointer',
    ].join(';');
    if (onClick) btn.addEventListener('click', onClick);
    return btn;
}
